import {
  type ItemListItem,
  itemStatusLabel,
  projectSummary,
  type Space,
} from "@archinventory/client";
import { formatItemNumber, type ItemFieldKey, itemStatuses } from "@archinventory/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { RowSelectionState } from "@tanstack/react-table";
import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  ImageOff,
  Images,
  LayoutGrid,
  ListChecks,
  Search,
  Table2,
  Trash2,
  Warehouse,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ItemForm } from "../../../components/item-form";
import { ItemsTable } from "../../../components/items-table";
import { Empty, Page } from "../../../components/page";
import { SpacesDialog } from "../../../components/spaces-dialog";
import { StatusBadge } from "../../../components/status-badge";
import { Button } from "../../../components/ui/button";
import { Input, Select } from "../../../components/ui/input";
import { Menu, MenuItem, MenuLink } from "../../../components/ui/menu";
import { Segmented } from "../../../components/ui/segmented";
import { api, qk, unwrap } from "../../../lib/api";
import { toastError, useCan, useMe } from "../../../lib/hooks";
import { usePatchItem } from "../../../lib/items";
import { spaceOptions } from "../../../lib/spaces";
import { cn } from "../../../lib/utils";

const search = z.object({
  view: z.enum(["table", "gallery", "review"]).optional().catch(undefined),
  q: z.string().optional(),
  status: z.string().optional(),
  spaceId: z.string().optional(),
});

export const Route = createFileRoute("/_app/projects/$projectId")({
  validateSearch: search,
  component: ProjectPage,
});

function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ProjectPage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const s = { ...search, view: search.view ?? "table" };
  const navigate = useNavigate({ from: Route.fullPath });
  const canManage = useCan("admin");
  const qc = useQueryClient();
  const [qInput, setQInput] = useState(s.q ?? "");
  const q = useDebounced(qInput);
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [spacesOpen, setSpacesOpen] = useState(false);

  useEffect(() => {
    if ((s.q ?? "") !== q)
      void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true });
  }, [q, s.q, navigate]);

  const project = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => unwrap(api.projects[":id"].$get({ param: { id: projectId } })),
  });
  const filter = {
    q: s.q,
    status: s.view === "review" ? "needs_review" : s.status,
    spaceId: s.spaceId,
  };
  const items = useQuery({
    queryKey: qk.items(projectId, filter),
    queryFn: () =>
      unwrap(
        api.projects[":id"].items.$get({
          param: { id: projectId },
          query: Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) as Record<
            string,
            string
          >,
        }),
      ),
    placeholderData: keepPreviousData,
  });
  // Unfiltered list for the header summary ("2 in progress · 7 to review").
  const all = useQuery({
    queryKey: qk.items(projectId),
    queryFn: () => unwrap(api.projects[":id"].items.$get({ param: { id: projectId }, query: {} })),
  });

  const archive = useMutation({
    mutationFn: (status: "active" | "archived") =>
      unwrap(api.projects[":id"].$patch({ param: { id: projectId }, json: { status } })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.project(projectId) });
      await qc.invalidateQueries({ queryKey: qk.projects });
    },
    onError: toastError,
  });

  if (project.isError) return <Page title="Project">Not found.</Page>;
  const p = project.data?.project;
  const spaces = project.data?.spaces ?? [];
  const list = items.data?.items ?? [];
  const selectedIds = Object.keys(selection).filter((k) => selection[k]);

  const setView = (view: "table" | "gallery" | "review") =>
    navigate({ search: (prev) => ({ ...prev, view }) });

  return (
    <Page
      title={p ? p.name : "…"}
      subtitle={
        p && (
          <>
            <Link to="/projects" className="hover:underline">
              {p.clientName}
            </Link>
            {p.siteAddress ? ` · ${p.siteAddress}` : ""} · {projectSummary(all.data?.items ?? [])}
            {p.status === "archived" && " · archived"}
          </>
        )
      }
      actions={
        <>
          <Button variant="outline" onClick={() => setSpacesOpen(true)}>
            <Warehouse /> Rooms
          </Button>
          <a href={`/api/projects/${projectId}/export.zip`} download tabIndex={-1}>
            <Button variant="outline">
              <Download /> Export project
            </Button>
          </a>
          <Menu label="More project actions">
            <MenuLink href={`/api/projects/${projectId}/export.zip?photos=false`} download>
              <FileSpreadsheet /> Export Excel only (no photos)
            </MenuLink>
            {canManage && p && (
              <MenuItem
                onClick={() => archive.mutate(p.status === "archived" ? "active" : "archived")}
              >
                {p.status === "archived" ? <ArchiveRestore /> : <Archive />}
                {p.status === "archived" ? "Unarchive project" : "Archive project"}
              </MenuItem>
            )}
          </Menu>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="View"
          value={s.view}
          onChange={setView}
          options={[
            { value: "table", label: "Table", icon: Table2 },
            { value: "gallery", label: "Gallery", icon: LayoutGrid },
            { value: "review", label: "Review", icon: ListChecks },
          ]}
        />
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search items"
            placeholder="Search name, manufacturer, #number…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="pl-8"
          />
        </div>
        {s.view !== "review" && (
          <Select
            aria-label="Filter by status"
            className="w-44"
            value={s.status ?? ""}
            onChange={(e) =>
              navigate({ search: (prev) => ({ ...prev, status: e.target.value || undefined }) })
            }
          >
            <option value="">All statuses</option>
            {itemStatuses.map((st) => (
              <option key={st} value={st}>
                {itemStatusLabel(st)}
              </option>
            ))}
          </Select>
        )}
        <Select
          aria-label="Filter by room"
          className="w-56"
          value={s.spaceId ?? ""}
          onChange={(e) =>
            navigate({ search: (prev) => ({ ...prev, spaceId: e.target.value || undefined }) })
          }
        >
          <option value="">All rooms</option>
          {spaceOptions(spaces).map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>

      {items.data && list.length === 0 && (
        <Empty>
          {s.view === "review"
            ? "Nothing to review. 🎉"
            : "No items yet. Capture them with the mobile app."}
        </Empty>
      )}
      {list.length > 0 && s.view === "table" && (
        <ItemsTable
          items={list}
          spaces={spaces}
          projectId={projectId}
          selection={selection}
          onSelectionChange={setSelection}
        />
      )}
      {list.length > 0 && s.view === "gallery" && <Gallery items={list} spaces={spaces} />}
      {list.length > 0 && s.view === "review" && <Review items={list} projectId={projectId} />}
      {selectedIds.length > 0 && s.view === "table" && (
        <SelectionBar
          items={list.filter((it) => selection[it.id])}
          projectId={projectId}
          onClear={() => setSelection({})}
        />
      )}
      <SpacesDialog
        projectId={projectId}
        spaces={spaces}
        open={spacesOpen}
        onClose={() => setSpacesOpen(false)}
      />
    </Page>
  );
}

/**
 * Floats at the bottom while table rows are selected, so the list doesn't jump. Selection is for
 * deleting several items; editing stays per item. Only an admin or an item's creator may delete
 * it (the API enforces this too), so other people's items are left out and counted.
 */
function SelectionBar({
  items,
  projectId,
  onClear,
}: {
  items: ItemListItem[];
  projectId: string;
  onClear: () => void;
}) {
  const me = useMe();
  const isAdmin = useCan("admin");
  const qc = useQueryClient();
  const deletable = items.filter((it) => isAdmin || it.createdBy === me.user.id);
  const notOwn = items.length - deletable.length;
  const remove = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        deletable.map((it) => unwrap(api.items[":id"].$delete({ param: { id: it.id } }))),
      );
      return results.filter((r) => r.status === "rejected").length;
    },
    onSuccess: async (failed) => {
      const done = deletable.length - failed;
      if (done) toast.success(`Deleted ${done} item${done === 1 ? "" : "s"}`);
      if (failed) toast.error(`${failed} item${failed === 1 ? "" : "s"} could not be deleted`);
      onClear();
      await qc.invalidateQueries({ queryKey: qk.itemsAll(projectId) });
      await qc.invalidateQueries({ queryKey: qk.projects });
    },
    onError: toastError,
  });
  const n = deletable.length;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-border bg-background py-1.5 pr-1.5 pl-4 text-sm shadow-lg">
        <span className="font-medium tabular-nums">{items.length} selected</span>
        {notOwn > 0 && (
          <span className="text-muted-foreground">({notOwn} by others, can't delete)</span>
        )}
        <Button
          size="sm"
          variant="destructive"
          className="rounded-full"
          disabled={n === 0 || remove.isPending}
          onClick={() =>
            confirm(
              `Delete ${n} item${n === 1 ? "" : "s"}? An admin can restore them for 30 days.`,
            ) && remove.mutate()
          }
        >
          <Trash2 /> Delete {n}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-8 rounded-full"
          aria-label="Clear selection"
          onClick={onClear}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

function Gallery({ items, spaces }: { items: ItemListItem[]; spaces: Space[] }) {
  const room = new Map(spaceOptions(spaces).map((o) => [o.id, o.label]));
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {items.map((it) => {
        const meta = [it.manufacturer, it.spaceId ? room.get(it.spaceId) : null].filter(Boolean);
        return (
          <Link
            key={it.id}
            to="/items/$itemId"
            params={{ itemId: it.id }}
            className="group flex flex-col overflow-hidden rounded-lg border border-border bg-background shadow-xs transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
              {it.photos[0] ? (
                <img
                  src={it.photos[0].thumbUrl}
                  alt={it.name ?? ""}
                  loading="lazy"
                  className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="flex size-full items-center justify-center text-muted-foreground/50">
                  <ImageOff className="size-8" />
                </div>
              )}
              <span className="absolute top-2 left-2 rounded-md bg-background/90 px-1.5 py-0.5 font-mono text-xs font-medium shadow-xs backdrop-blur">
                {formatItemNumber(it.number)}
              </span>
              {it.photos.length > 1 && (
                <span className="absolute top-2 right-2 flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-xs text-white backdrop-blur">
                  <Images className="size-3" /> {it.photos.length}
                </span>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-1.5 p-3">
              <span
                className={cn(
                  "line-clamp-2 text-sm leading-snug font-medium",
                  !it.name && "text-muted-foreground",
                )}
              >
                {it.name ?? "Unnamed item"}
              </span>
              {meta.length > 0 && (
                <span className="truncate text-xs text-muted-foreground">{meta.join(" · ")}</span>
              )}
              <div className="mt-auto pt-1">
                <StatusBadge status={it.status} aiError={it.aiError} stage={it.analysisStage} />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/** Review mode: large photos left, form right. Ctrl/⌘+Enter = confirm → next; ←/→ photos; J/K items. */
function Review({ items, projectId }: { items: ItemListItem[]; projectId: string }) {
  const [index, setIndex] = useState(0);
  const [photo, setPhoto] = useState(0);
  const item = items[Math.min(index, items.length - 1)];
  const patch = usePatchItem(projectId);
  const members = useQuery({ queryKey: qk.members, queryFn: () => unwrap(api.org.members.$get()) });
  const detail = useQuery({
    queryKey: qk.item(item?.id ?? ""),
    queryFn: () => unwrap(api.items[":id"].$get({ param: { id: item?.id ?? "" } })),
    enabled: !!item,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowRight") setPhoto((p) => p + 1);
      if (e.key === "ArrowLeft") setPhoto((p) => Math.max(0, p - 1));
      if (e.key === "j") setIndex((i) => Math.min(items.length - 1, i + 1));
      if (e.key === "k") setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length]);

  const photos = useMemo(() => detail.data?.item.photos ?? item?.photos ?? [], [detail.data, item]);
  if (!item) return null;
  const current = photos[Math.min(photo, photos.length - 1)];

  return (
    <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
      <div className="flex flex-col gap-2">
        <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg bg-muted">
          {current && (
            <img src={current.url} alt="" className="max-h-full max-w-full object-contain" />
          )}
          {photos.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-2"
                aria-label="Previous photo"
                onClick={() => setPhoto((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-2"
                aria-label="Next photo"
                onClick={() => setPhoto((p) => Math.min(photos.length - 1, p + 1))}
              >
                <ChevronRight />
              </Button>
            </>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {photos.map((ph, i) => (
            <button
              key={ph.id}
              type="button"
              onClick={() => setPhoto(i)}
              className={cn(
                "size-16 shrink-0 overflow-hidden rounded border-2",
                i === photo ? "border-primary" : "border-transparent",
              )}
            >
              <img src={ph.thumbUrl} alt="" className="size-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Link
            to="/items/$itemId"
            params={{ itemId: item.id }}
            className="font-mono text-lg text-primary hover:underline"
          >
            {formatItemNumber(item.number)}
          </Link>
          <span className="text-sm text-muted-foreground">
            {index + 1} / {items.length} · <kbd>J</kbd>/<kbd>K</kbd> · <kbd>Ctrl</kbd>+
            <kbd>Enter</kbd> confirm
          </span>
        </div>
        {detail.data && (
          <ItemForm
            key={item.id}
            item={detail.data.item}
            members={members.data?.members}
            autoFocus
            saving={patch.isPending}
            saveLabel="Confirm & next"
            onSave={(fields, confirm) =>
              patch.mutate(
                {
                  id: item.id,
                  patch: { fields: fields as Partial<Record<ItemFieldKey, never>>, confirm },
                },
                {
                  onSuccess: () => {
                    setPhoto(0);
                    setIndex((i) => Math.min(items.length - 1, i + 1));
                  },
                },
              )
            }
          />
        )}
      </div>
    </div>
  );
}
